import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SERVICES, PRICING_TIERS, HOW_IT_WORKS, TESTIMONIALS, CONTACT_INFO } from '../data/mock';
import { CalendlyEmbed } from '../components/CalendlyEmbed';
import { PromoBanner } from '../components/PromoBanner';
import { Badge } from '../components/ui/badge';
import * as Icons from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const stagger = {
  initial: {},
  whileInView: {},
  viewport: { once: true },
  transition: { staggerChildren: 0.08 },
};

export default function HomePage() {
  return (
    <main>
      {/* Promotional Banner */}
      <div className="pt-16">
        <PromoBanner />
      </div>

      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden"
        style={{ background: 'radial-gradient(at 53% 78%, hsla(60,100%,50%,0.3) 0px, transparent 50%), radial-gradient(at 71% 91%, hsla(108,100%,50%,0.3) 0px, transparent 50%), radial-gradient(at 31% 91%, hsla(30,100%,50%,0.17) 0px, transparent 50%)' }}>
        <div className="max-w-[800px] mx-auto px-6 pt-28 pb-16 text-center">
          <motion.div {...fadeInUp}>
            <Badge className="mb-6 bg-[rgba(148,242,127,0.12)] text-[rgb(13,121,22)] border-[rgba(148,242,127,0.3)] px-4 py-1.5 text-xs font-medium rounded-full">
              20+ Years of Experience · Phoenix, AZ Headquarters
            </Badge>
          </motion.div>
          <motion.h1 {...fadeInUp} transition={{ delay: 0.1, duration: 0.5 }}
            className="font-bold text-[rgb(0,55,32)] leading-[0.92] tracking-tight mb-6"
            style={{ fontSize: 'clamp(2.25rem, 5vw, 4.2rem)' }}>
            Nationwide Tax Prep + Business Structure From Beginning to End
          </motion.h1>
          <motion.p {...fadeInUp} transition={{ delay: 0.2, duration: 0.5 }}
            className="text-[rgb(131,146,140)] mb-8 max-w-[600px] mx-auto leading-relaxed"
            style={{ fontSize: 'clamp(1.05rem, 2.2vw, 1.2rem)' }}>
            Fast 2-day turnaround after docs are received. Clear pricing. Nationwide service.
          </motion.p>
          <motion.div {...fadeInUp} transition={{ delay: 0.3, duration: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/book"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-base shadow-sm hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 no-underline">
              Book Free Consult
              <Icons.ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/start-here"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-black/10 text-[rgb(0,55,32)] font-medium text-base no-underline hover:bg-black/5 transition-colors duration-200">
              Start Here
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-8 border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 text-sm text-[rgb(131,146,140)]">
            <span className="flex items-center gap-2"><Icons.ShieldCheck className="w-4 h-4 text-[#8FEC78]" /> IRS Compliant</span>
            <span className="flex items-center gap-2"><Icons.Zap className="w-4 h-4 text-[#8FEC78]" /> 2-Day Turnaround</span>
            <span className="flex items-center gap-2"><Icons.Globe className="w-4 h-4 text-[#8FEC78]" /> All 50 States</span>
            <span className="flex items-center gap-2"><Icons.Lock className="w-4 h-4 text-[#8FEC78]" /> Secure & Confidential</span>
            <span className="flex items-center gap-2"><Icons.DollarSign className="w-4 h-4 text-[#8FEC78]" /> Starting at $295</span>
          </div>
        </div>
      </section>

      {/* Services Snapshot */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <p className="text-[rgb(13,121,22)] text-sm font-medium mb-2 uppercase tracking-wider">Our Services</p>
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.75rem)', lineHeight: '0.95' }}>Everything You Need, Tax Season & Beyond</h2>
          </motion.div>
          <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.slice(0, 5).map((service, i) => {
              const Icon = Icons[service.icon] || Icons.FileText;
              return (
                <motion.div key={service.id} {...fadeInUp} transition={{ delay: i * 0.08 }}
                  className="group bg-white border border-black/10 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer">
                  <div className="w-10 h-10 rounded-lg bg-[rgba(148,242,127,0.12)] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[rgb(13,121,22)]" />
                  </div>
                  <h3 className="font-semibold text-[rgb(0,55,32)] text-base mb-2">{service.shortTitle}</h3>
                  <p className="text-sm text-[rgb(131,146,140)] leading-relaxed mb-3">{service.description}</p>
                  <span className="text-sm font-medium text-[rgb(13,121,22)]">{service.price}</span>
                </motion.div>
              );
            })}
          </motion.div>
          <div className="text-center mt-8">
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-[rgb(13,121,22)] hover:underline no-underline">
              View all services <Icons.ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-14">
            <p className="text-[rgb(13,121,22)] text-sm font-medium mb-2 uppercase tracking-wider">How It Works</p>
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.75rem)', lineHeight: '0.95' }}>Four Simple Steps to Filed Taxes</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((step, i) => {
              const Icon = Icons[step.icon] || Icons.CheckCircle;
              return (
                <motion.div key={step.step} {...fadeInUp} transition={{ delay: i * 0.1 }}
                  className="relative text-center">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-[rgb(131,146,140)] uppercase tracking-wider">Step {step.step}</span>
                  <h3 className="font-semibold text-[rgb(0,55,32)] text-base mt-1 mb-2">{step.title}</h3>
                  <p className="text-sm text-[rgb(131,146,140)] leading-relaxed">{step.description}</p>
                  {i < HOW_IT_WORKS.length - 1 && (
                    <div className="hidden lg:block absolute top-7 left-[calc(50%+40px)] w-[calc(100%-80px)] border-t border-dashed border-black/10" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <p className="text-[rgb(13,121,22)] text-sm font-medium mb-2 uppercase tracking-wider">Transparent Pricing</p>
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.75rem)', lineHeight: '0.95' }}>Clear Pricing. No Surprises.</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRICING_TIERS.map((tier, i) => (
              <motion.div key={tier.title} {...fadeInUp} transition={{ delay: i * 0.08 }}
                className={`rounded-xl p-6 border transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${
                  tier.highlight ? 'border-[#8FEC78] bg-[rgba(148,242,127,0.04)] shadow-sm' : 'border-black/10 bg-white'
                }`}>
                {tier.highlight && (
                  <Badge className="mb-3 bg-[rgba(148,242,127,0.15)] text-[rgb(13,121,22)] border-transparent text-xs rounded-full">Most Popular</Badge>
                )}
                <h3 className="font-semibold text-[rgb(0,55,32)] text-lg mb-1">{tier.title}</h3>
                <div className="flex items-baseline gap-1 mb-3">
                  {tier.prefix && <span className="text-sm text-[rgb(131,146,140)]">{tier.prefix}</span>}
                  <span className="text-2xl font-bold text-[rgb(0,55,32)]">{tier.price}</span>
                </div>
                <p className="text-sm text-[rgb(131,146,140)] mb-4 leading-relaxed">{tier.description}</p>
                <Link to="/book" className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm no-underline hover:shadow-md transition-shadow">
                  {tier.cta}
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing" className="inline-flex items-center gap-2 text-sm font-medium text-[rgb(13,121,22)] hover:underline no-underline">
              See full pricing details <Icons.ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <p className="text-[rgb(13,121,22)] text-sm font-medium mb-2 uppercase tracking-wider">Client Feedback</p>
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.75rem)', lineHeight: '0.95' }}>What Our Clients Are Saying</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.slice(0, 6).map((t, i) => (
              <motion.div key={i} {...fadeInUp} transition={{ delay: i * 0.06 }}
                className="bg-white border border-black/10 rounded-xl p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, j) => <Icons.Star key={j} className="w-4 h-4 fill-[#8FEC78] text-[#8FEC78]" />)}
                </div>
                <p className="text-sm text-[rgb(14,15,12)] leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[rgba(148,242,127,0.15)] flex items-center justify-center">
                    <span className="text-xs font-semibold text-[rgb(13,121,22)]">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[rgb(0,55,32)]">{t.name}</p>
                    <p className="text-xs text-[rgb(131,146,140)]">{t.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[900px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <p className="text-[rgb(13,121,22)] text-sm font-medium mb-2 uppercase tracking-wider">Ready to Get Started?</p>
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight mb-3" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.75rem)', lineHeight: '0.95' }}>Book Your Free Consultation</h2>
            <p className="text-[rgb(131,146,140)] text-base">Pick a time that works for you. No obligation, no pressure.</p>
          </motion.div>
          <motion.div {...fadeInUp} className="rounded-2xl border border-black/10 overflow-hidden bg-white shadow-sm">
            <CalendlyEmbed />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
